import { CommonModule, DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { Subject } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { MenuModule, Menu } from "primeng/menu";
import { MenuItem } from "primeng/api";
import { RippleModule } from "primeng/ripple";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";

// Models
import {
  ProductGroup,
  ProductGroupProduct,
} from "../../../../core/services/qpilot.service";
import {
  IProductGroupListResponse,
  IProductGroupRowAction,
  IProductGroupPageEvent,
  IProductGroupSortEvent,
} from "../../interfaces/product-group.interface";

// Components
import {
  TableSkeletonRowsComponent,
  SkeletonColumnConfig,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";

/**
 * Page size options for pagination
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * Product Groups Table Component
 * PrimeNG p-table implementation with selection and row actions
 */
@Component({
  selector: "app-product-groups-table",
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TableModule,
    ButtonModule,
    MenuModule,
    RippleModule,
    SkeletonModule,
    TooltipModule,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./product-groups-table.component.html",
  styleUrl: "./product-groups-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupsTableComponent implements OnInit, OnDestroy {
  // Input properties
  @Input() data: IProductGroupListResponse | null = null;
  @Input() loading = false;
  @Input() showActions = true;
  @Input() scrollHeight = "flex";
  @Input() siteUtcOffset = 0;
  @Input() siteId: number | null = null;

  // Expanded rows state
  expandedRows: { [key: number]: boolean } = {};

  // Output events
  @Output() sortChanged = new EventEmitter<IProductGroupSortEvent>();
  @Output() pageChanged = new EventEmitter<IProductGroupPageEvent>();
  @Output() rowAction = new EventEmitter<IProductGroupRowAction>();

  // Component state
  private readonly destroy$ = new Subject<void>();
  contextMenuItems: MenuItem[] = [];
  currentContextGroup: ProductGroup | null = null;

  // Page size options for template
  readonly pageSizeOptions = [...PAGE_SIZE_OPTIONS];

  // Skeleton columns configuration using preset
  readonly skeletonColumns: SkeletonColumnConfig[] =
    SKELETON_PRESETS.productGroups;

  ngOnInit(): void {
    this.initializeContextMenu();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize context menu items for row actions
   */
  private initializeContextMenu(): void {
    this.contextMenuItems = [
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => this.emitRowAction("edit"),
      },
      { separator: true },
      {
        label: "Delete",
        icon: "pi pi-trash",
        styleClass: "text-red-500",
        command: () => this.emitRowAction("delete"),
      },
    ];
  }

  /**
   * Emit row action with current context group
   */
  private emitRowAction(action: IProductGroupRowAction["action"]): void {
    if (this.currentContextGroup) {
      this.rowAction.emit({
        action,
        group: this.currentContextGroup,
      });
    }
  }

  // ==================== Pagination & Sorting ====================

  /**
   * Handle sort change
   */
  onSort(event: { field: string; order: number }): void {
    this.sortChanged.emit({
      field: event.field,
      order: event.order as 1 | -1,
    });
  }

  /**
   * Handle page change
   */
  onPage(event: { first: number; rows: number }): void {
    const page = Math.floor(event.first / event.rows) + 1;
    this.pageChanged.emit({
      first: event.first,
      rows: event.rows,
      page,
    });
  }

  // ==================== Row Actions ====================

  /**
   * Show context menu for a row
   */
  onRowContextMenu(event: MouseEvent, menu: Menu, group: ProductGroup): void {
    this.currentContextGroup = group;
    menu.toggle(event);
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Handle edit action
   */
  onEdit(group: ProductGroup): void {
    this.rowAction.emit({ action: "edit", group });
  }

  /**
   * Handle delete action
   */
  onDelete(group: ProductGroup): void {
    this.rowAction.emit({ action: "delete", group });
  }

  // ==================== Utility Methods ====================

  /**
   * Format date with site UTC offset
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      // Apply site UTC offset
      const offsetMs = this.siteUtcOffset * 60 * 60 * 1000;
      const adjustedDate = new Date(date.getTime() + offsetMs);
      return adjustedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  }

  /**
   * TrackBy function for table rows
   */
  trackByGroupId(index: number, group: ProductGroup): number {
    return group.ProductGroupId;
  }

  // ==================== Expandable Row Methods ====================

  /**
   * Event handler for row expansion
   */
  onRowExpand(event: { data: ProductGroup }): void {
    // Single row mode - handled by PrimeNG
    this.expandedRows = { [event.data.ProductGroupId]: true };
  }

  /**
   * Event handler for row collapse
   */
  onRowCollapse(event: { data: ProductGroup }): void {
    delete this.expandedRows[event.data.ProductGroupId];
  }

  /**
   * Toggle row expansion (for row click)
   */
  toggleRowExpansion(group: ProductGroup): void {
    if (this.expandedRows[group.ProductGroupId]) {
      delete this.expandedRows[group.ProductGroupId];
    } else {
      // Single row mode - collapse others and expand this one
      this.expandedRows = { [group.ProductGroupId]: true };
    }
  }

  /**
   * Get products to display (max 6 for preview)
   */
  getDisplayedProducts(group: ProductGroup): ProductGroupProduct[] {
    return group.Products?.slice(0, 6) || [];
  }

  /**
   * Get URL to view product detail page
   */
  getProductViewUrl(product: ProductGroupProduct): string {
    if (!this.siteId) return "#";
    // Navigate to product detail page
    return `/widgets/dashboard/${this.siteId}/products/${product.Id}`;
  }

  /**
   * Get URL to search for product by title
   */
  getProductSearchUrl(product: ProductGroupProduct): string {
    if (!this.siteId) return "#";
    // Search URL with product title
    return `/widgets/dashboard/${this.siteId}/products?search=${encodeURIComponent(product.Title || product.Id)}`;
  }

  /**
   * Navigate to product detail page
   */
  onViewProduct(product: ProductGroupProduct): void {
    this.rowAction.emit({
      action: "viewProduct" as IProductGroupRowAction["action"],
      group: { ProductGroupId: 0, Name: "" } as ProductGroup,
      productId: product.Id,
    });
  }

  /**
   * Navigate to products page with search
   */
  onSearchProduct(product: ProductGroupProduct): void {
    this.rowAction.emit({
      action: "searchProduct" as IProductGroupRowAction["action"],
      group: { ProductGroupId: 0, Name: "" } as ProductGroup,
      productSearch: product.Title || product.Id,
    });
  }

  /**
   * Handle edit group action (to show all products)
   */
  onEditGroup(group: ProductGroup): void {
    this.rowAction.emit({ action: "edit", group });
  }
}
